/**
 * Observes when a sticky element un/becomes sticky and triggers a callback
 *
 * Add this to any sticky element to have a class added to it when it becomes fixed:
 * data-sticky-observer="{json options}"
 *
 * Can use API or data attributes.
 *
 * Requires a parent container element.  The container must have some kind of `position` value set,
 * if not, it will be set to `relative.`
 *
 * See defaults for options.
 *
 * Uses the technique at https://developers.google.com/web/updates/2017/09/sticky-headers
 *
 * Two elements are added to the parent container, one absolutely positioned at the top, another at
 * the bottom. Observers are placed on each. When those trigger, then we know if the sticky element
 * is inline or sticky.
 *
 * ```Polyfills required:
 * IntersectionObserver     IE, older browsers
 * ResizeObserver           IE, Edge, older browsers
 * classList                IE
 * Element.prototype.remove IE
 * ```
 */

// console.log('Site:stickyObserver');

const defaults = {
  callback              : null,   // Callback function
  viewport              : null,   // Element the intersection checks against, should be the closest scrollable element, or null for the viewport
  rootMargin            : '0px',  // Can have values similar to the CSS margin property, e.g. "10px 20px 30px 40px" (top, right, bottom, left). The values can be percentages.
  onStateChangeCallback : null,
  stickyClass           : 'sticky',
  stickyBottomClass     : 'bottom',
  sentinelClass         : 'sentinel',
};


export class StickyObserver {
  options;
  element;
  topSentinel;
  bottomSentinel;
  topObserver;
  bottomObserver;
  elementObserver;


  /**
   * Constructs a new StickyObervser.
   *
   * @param {object} options The options. See the defaults.
   */
  constructor(element, options) {
    // console.log('StickyObserver:constructor');
    // console.log('element', element);

    if (!('IntersectionObserver' in window) || !('ResizeObserver' in window)) {
      return;
    }

    this.element = element;
    this.options = {...defaults, ...options};

    this.init();
  }


  init() {
    // console.log('StickyObserver:init');

    const parentElement = this.element.parentElement;

    if (!parentElement.style.position) {
      parentElement.style.position = 'relative';
    }

    // Add sentinels
    this.topSentinel = document.createElement('div');
    this.topSentinel.className = this.options.sentinelClass + ' top';
    this.topSentinel.style.position = 'absolute';
    this.topSentinel.style.top = 0;
    this.topSentinel.style.width = '100%';
    // this.topSentinel.style.background = 'red';
    // this.topSentinel.style.zIndex = 99;
    // this.topSentinel.style.opacity = 0.5;

    parentElement.appendChild(this.topSentinel);

    this.bottomSentinel = document.createElement('div');
    this.bottomSentinel.className = this.options.sentinelClass + ' bottom';
    this.bottomSentinel.style.position = 'absolute';
    this.bottomSentinel.style.bottom = 0;
    this.bottomSentinel.style.width = '100%';
    // this.bottomSentinel.style.background = 'blue';
    // this.bottomSentinel.style.zIndex = 99;
    // this.bottomSentinel.style.opacity = 0.5;

    parentElement.appendChild(this.bottomSentinel);

    // Observe sentinels
    this.topObserver = new IntersectionObserver(this.sentinelCallback.bind(this), {
      root       : this.options.viewport,
      threshold  : [1],
      rootMargin : this.options.rootMargin,
    });
    this.topObserver.observe(this.topSentinel);

    this.bottomObserver = new IntersectionObserver(this.sentinelCallback.bind(this), {
      root       : this.options.viewport,
      threshold  : [1],
      rootMargin : this.options.rootMargin,
    });
    this.bottomObserver.observe(this.bottomSentinel);

    this.elementObserver = new ResizeObserver(this.resizeCallback.bind(this));
    this.elementObserver.observe(this.element);
  }


  destroy() {
    // console.log('StickyObserver:destroy');

    this.topObserver.disconnect();
    this.bottomObserver.disconnect();
    this.elementObserver.disconnect();
    this.topSentinel.remove();
    this.bottomSentinel.remove();
  }


  resizeCallback(entries, observer) {
    // console.log('StickyObserver:resizeCallback');
    // console.log('entries', entries);

    this.topSentinel.style.height = this.element.offsetHeight + 'px';
    this.bottomSentinel.style.height = this.element.offsetHeight + 'px';
  }


  sentinelCallback(entries, observer) {
    // console.log('StickyObserver:sentinelCallback');
    // console.log('entries', entries);

    for (const entry of entries) {
      // Note: When scrolling quickly, the observer can trigger when the element is no longer intersecting, and isIntersecting is false
      // So can't rely on it
      // if (!entry.isIntersecting) {
      //   continue;
      // }

      const sentinelRect = entry.boundingClientRect;
      const rootBounds = entry.rootBounds;

      // console.log('sentinelRect', sentinelRect);

      if (entry.target === this.topSentinel) {
        if (sentinelRect.top < 0) {
          // console.log('Sticky start - top');
          this.onStickyStart(true);

          continue;
        }

        if (sentinelRect.top >= 0) {
          // console.log('Sticky stop - top');
          this.onStickyStop(true);
        }
      } else {
        // console.log('top', sentinelRect.top, 'height', sentinelRect.height, 'total', sentinelRect.top + sentinelRect.height, 'root height', rootBounds.height, 'intersectionRatio', entry.intersectionRatio, entry.boundingClientRect.y < entry.rootBounds.y ); //                                 sentinelRect.top >= 0, sentinelRect.top + sentinelRect.height < rootBounds.height, sentinelRect.top + sentinelRect.height < rootBounds.height);

        if (sentinelRect.top < rootBounds.y) {
          // Page scrolling past end point
          // console.log('Sticky stop - bottom');
          this.onStickyStop(false);

          continue;
        }

        // console.log('entry', entry);

        // Only care when the bottom sentinel intersect with the top of the viewport
        //  && sentinelRect.top - sentinelRect.height <= 0
        // Use rootBounds.height / 2 as a quick way to determine if the sentinel crossed the top of the page. This can fail if scrolling very quickly.
        if (sentinelRect.top >= rootBounds.y && sentinelRect.top + sentinelRect.height < rootBounds.height / 2 && sentinelRect.top + sentinelRect.height < rootBounds.height) {
          // Page scrolling before end point
          // console.log('Sticky start - bottom');
          this.onStickyStart(false);
        }
      }
    }
  }


  onStickyStart(top) {
    // console.log('StickyObserver:onStickyStart');

    this.element.classList.add(this.options.stickyClass);
    this.element.classList.remove(this.options.stickyBottomClass);

    if (this.options.callback) {
      this.options.callback({
        instance : this,
        sticky   : true,
        top,
      });
    }
  }


  onStickyStop(top) {
    // console.log('StickyObserver:onStickyStop');

    this.element.classList.remove(this.options.stickyClass);

    if (top) {
      this.element.classList.remove(this.options.stickyBottomClass);
    } else {
      this.element.classList.add(this.options.stickyBottomClass);
    }

    if (this.options.callback) {
      this.options.callback({
        instance : this,
        sticky   : false,
        top,
      });
    }
  }
}


document.querySelectorAll('[data-sticky-observer]').forEach((element) => {
  const data = element.dataset.stickyObserver;

  let options = {};

  if (data && data[0] === '{') {
    try {
      options = JSON.parse(data);

      if (typeof options !== 'object') {
        throw new Error('StickyObserver: Not an object');
      }
    } catch (e) {
      // console.log('e', e);
      options = {};
    }
  }

  // options.callback = (data) => {
  //   console.log('Callback test, sticky', data.sticky, 'top', data.top);
  //   // data.instance.destroy();
  // };

  // console.log('options', options);

  /* eslint-disable-next-line */
  new StickyObserver(element, options);
});
